import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Modal, PillButton, Chip } from '../ui';
import { KEY_TYPES, generateKeyPair, inspectKey, looksPrivate } from '../utils/sshKey';
import { ReactComponent as KeyRoundIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as UploadIcon } from '../assets/icons/upload.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import './SshKeyEditor.css';

const MAX_FILE_BYTES = 64 * 1024;
// Nombres tecnicos: no se traducen.
const TYPE_LABEL = { ed25519: 'Ed25519', 'ecdsa-p256': 'ECDSA P-256', 'ecdsa-p384': 'ECDSA P-384', 'rsa-3072': 'RSA 3072', 'rsa-4096': 'RSA 4096' };
const signature = (priv, pub, pass) => JSON.stringify([priv, pub, pass]);

/**
 * Editor de un item `ssh`: clave privada (oculta con mostrar), passphrase, clave publica, y la huella y el tipo, que se
 * leen solos. Es controlado: `values` es el formulario y `onChange(patch)` mezcla campos en el.
 *
 * - Generar: ed25519 (o ECDSA P-256 si el navegador no la soporta), ECDSA P-384, RSA 3072/4096, en este navegador.
 *   Si ya hay una passphrase escrita, la privada nueva sale cifrada con ella.
 * - Importar: pegando o subiendo ficheros (id_*, *.pub). Al cambiar la privada, la publica o la passphrase, el servidor
 *   la lee (formato, publica, huella, tipo, passphrase): una sola implementacion para el panel y la app.
 * El servidor vuelve a calcular la huella y el tipo al guardar: lo que se ve aqui es una comodidad, no la fuente.
 */
function SshKeyEditor({ values, onChange, itemTitle }) {
  const { t } = useTranslation();
  const priv = values.privateKey || '';
  const pub = values.publicKey || '';
  const pass = values.passphrase || '';

  const [showPrivate, setShowPrivate] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [genType, setGenType] = useState('ed25519');
  const [generating, setGenerating] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [fellBack, setFellBack] = useState(false);
  const [status, setStatus] = useState({ kind: 'idle' });
  const inspected = useRef(signature(priv, pub, pass)); // lo ya comprobado: no se repite (ni se entra en bucle al rellenar la publica)
  const requestId = useRef(0);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!priv.trim() && !pub.trim()) { setStatus({ kind: 'idle' }); return undefined; }
    if (inspected.current === signature(priv, pub, pass)) return undefined;
    const mine = ++requestId.current;
    setStatus({ kind: 'checking' });
    const timer = setTimeout(async () => {
      const info = await inspectKey({ privateKey: priv.trim(), publicKey: pub.trim(), passphrase: pass });
      if (mine !== requestId.current) return;
      if (!info.ok) {
        inspected.current = signature(priv, pub, pass);
        setStatus({ kind: 'error', detail: info.detail });
        onChange({ fingerprint: '', keyType: '' });
        return;
      }
      const nextPub = info.publicKey || pub;
      inspected.current = signature(priv, nextPub, pass);
      onChange({
        publicKey: nextPub,
        fingerprint: info.fingerprint || '',
        keyType: info.keyType || '',
        ...(info.comment && !values.comment ? { comment: info.comment } : {}),
      });
      if (info.privateKey.needsPassphrase) setStatus({ kind: 'needsPassphrase' });
      else if (info.privateKey.passphraseOk) setStatus({ kind: 'passphraseOk' });
      else if (info.source === 'private' && !pub.trim()) setStatus({ kind: 'derived' });
      else setStatus({ kind: 'ok' });
    }, 500);
    return () => clearTimeout(timer);
    // onChange y values.comment cambian en cada render del formulario; lo que decide una comprobacion nueva son los tres textos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priv, pub, pass]);

  const generate = async () => {
    setConfirmReplace(false);
    setGenerating(true);
    setFellBack(false);
    try {
      const key = await generateKeyPair({ keyType: genType, passphrase: pass, comment: values.comment || itemTitle || '' });
      requestId.current++; // una comprobacion en vuelo ya no vale
      inspected.current = signature(key.privateKey, key.publicKey, pass);
      onChange({
        privateKey: key.privateKey, publicKey: key.publicKey, fingerprint: key.fingerprint, keyType: key.keyType,
        ...(key.comment && !values.comment ? { comment: key.comment } : {}),
      });
      setFellBack(key.fellBack);
      setShowPrivate(false);
      setStatus({ kind: 'generated' });
    } catch {
      setStatus({ kind: 'genError' });
    } finally {
      setGenerating(false);
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const patch = {};
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) { setStatus({ kind: 'error', detail: 'file_too_big' }); return; }
      const text = (await file.text()).trim();
      if (looksPrivate(text)) patch.privateKey = text; else patch.publicKey = text;
    }
    if (Object.keys(patch).length) { setShowPrivate(false); onChange(patch); }
  };

  const statusTone = status.kind === 'error' || status.kind === 'genError' ? 'error' : status.kind === 'needsPassphrase' ? 'warn' : 'info';
  const statusText = (() => {
    if (status.kind === 'idle') return '';
    if (status.kind === 'error') return t(`ssh.errors.${status.detail}`, t('ssh.errors.request_failed'));
    return t(`ssh.status.${status.kind}`);
  })();

  return (
    <div className="sgc-ssh-editor">
      <div className="sgc-ssh-editor__generate">
        <div className="sgc-ssh-editor__generate-head">
          <h2 className="sgc-ssh-editor__title">{t('ssh.generateTitle')}</h2>
          <PillButton variant="secondary" size="sm" icon={UploadIcon} onClick={() => fileInput.current?.click()}>{t('ssh.importFiles')}</PillButton>
          <input ref={fileInput} type="file" multiple hidden onChange={onFiles} data-testid="ssh-file-input" />
        </div>
        <div className="sgc-ssh-editor__types" role="radiogroup" aria-label={t('ssh.keyType')}>
          {KEY_TYPES.map((k) => (
            <Chip
              key={k}
              label={k === 'ed25519' ? `${TYPE_LABEL[k]} (${t('ssh.recommended')})` : TYPE_LABEL[k]}
              selected={genType === k}
              onClick={() => setGenType(k)}
            />
          ))}
        </div>
        <p className="sgc-ssh-editor__hint">{t('ssh.generateHelp')} {t('ssh.importHelp')}</p>
        <div>
          <PillButton icon={KeyRoundIcon} loading={generating} onClick={() => (priv.trim() ? setConfirmReplace(true) : generate())}>
            {generating ? t('ssh.generating') : t('ssh.generate')}
          </PillButton>
        </div>
        {fellBack && (
          <p className="sgc-ssh-editor__note" role="status"><InfoIcon width={16} height={16} aria-hidden="true" /> {t('ssh.fellBack')}</p>
        )}
      </div>

      <div className="sgc-ssh-editor__field">
        <label htmlFor="sgc-ssh-private">{t('ssh.privateKey')}</label>
        {priv && !showPrivate ? (
          <div className="sgc-ssh-editor__masked">
            <span className="sgc-ssh-editor__dots" aria-hidden="true">••••••••••••••••••••</span>
            <button type="button" onClick={() => setShowPrivate(true)} title={t('dashboard.reveal')}><EyeIcon width={16} height={16} aria-hidden="true" /></button>
          </div>
        ) : (
          <div className="sgc-ssh-editor__reveal">
            <textarea
              id="sgc-ssh-private"
              className="sgc-ssh-editor__textarea"
              value={priv}
              rows={6}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="off"
              placeholder={t('dataEntry.sshKeyPlaceholder')}
              onChange={(e) => onChange({ privateKey: e.target.value })}
            />
            {priv && (
              <button type="button" onClick={() => setShowPrivate(false)} title={t('dashboard.reveal')}><EyeOffIcon width={16} height={16} aria-hidden="true" /></button>
            )}
          </div>
        )}
      </div>

      <Input
        label={t('ssh.passphrase')}
        type={showPass ? 'text' : 'password'}
        value={pass}
        autoComplete="off"
        onChange={(e) => onChange({ passphrase: e.target.value })}
        rightAction={(
          <button type="button" onClick={() => setShowPass((v) => !v)} title={t('dashboard.reveal')}>
            {showPass ? <EyeOffIcon width={16} height={16} aria-hidden="true" /> : <EyeIcon width={16} height={16} aria-hidden="true" />}
          </button>
        )}
      />
      <p className="sgc-ssh-editor__hint">{t('ssh.passphraseHelp')}</p>

      <div className="sgc-ssh-editor__field">
        <label htmlFor="sgc-ssh-public">{t('ssh.publicKey')}</label>
        <textarea
          id="sgc-ssh-public"
          className="sgc-ssh-editor__textarea"
          value={pub}
          rows={3}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          onChange={(e) => onChange({ publicKey: e.target.value })}
        />
      </div>

      {(values.fingerprint || values.keyType) && (
        <dl className="sgc-ssh-editor__derived">
          {values.keyType && (<><dt>{t('ssh.keyType')}</dt><dd>{values.keyType}</dd></>)}
          {values.fingerprint && (<><dt>{t('ssh.fingerprint')}</dt><dd className="sgc-ssh-editor__mono">{values.fingerprint}</dd></>)}
        </dl>
      )}

      {statusText && (
        <p className={`sgc-ssh-editor__status sgc-ssh-editor__status--${statusTone}`} role={statusTone === 'error' ? 'alert' : 'status'}>
          {statusTone === 'info' ? <InfoIcon width={16} height={16} aria-hidden="true" /> : <AlertTriangleIcon width={16} height={16} aria-hidden="true" />}
          {statusText}
        </p>
      )}

      <Input
        label={t('ssh.comment')}
        value={values.comment || ''}
        autoComplete="off"
        onChange={(e) => onChange({ comment: e.target.value })}
      />

      <Modal open={confirmReplace} onClose={() => setConfirmReplace(false)} title={t('ssh.replaceTitle')}>
        <p>{t('ssh.replaceBody')}</p>
        <div className="sgc-ssh-editor__confirm-actions">
          <PillButton variant="secondary" onClick={() => setConfirmReplace(false)}>{t('common.cancel')}</PillButton>
          <PillButton variant="danger" onClick={generate}>{t('ssh.replace')}</PillButton>
        </div>
      </Modal>
    </div>
  );
}

export default SshKeyEditor;
